import React, {
  useEffect, useState, useMemo, useCallback
} from 'react';
import { useTranslation } from 'react-i18next';
import queryString from 'query-string';
import classnames from 'classnames';
import { useLocation } from 'react-router-dom';
import { purchaseDetailsTypes } from '../../../../common/config/const';
import CreditCardProcessing from '../../purchases/credit_card_processing';
import PayPalProcessing from '../../purchases/pay_pal_processing';
import {
  getPurchaseURL, applePayIsSupported
} from '../../../../common/purchase_details_common/purchase_details_common_utils';
import Const, { contextToChatType } from '../../../config/const';
import classes from './classes.module.scss';
import ProcessApplePay from '../../../../common/payment_options/process_apple_pay';
import MobileProcessingStatus from '../../purchases/processing_status/mobile_processing_status';
import { getPurchaseClickSource } from '../../purchases/purchases_utils';
import { toCamelCase, getClickSource } from '../../../../common/config/utils';
import LoadingIndicator from '../../../../common/helpers/loading_indicator';

const getForMobileBuyBtnClassName = ({ purchaseButtonLoading, disableBuyButton, purchaseButtonTitleClassName }: { purchaseButtonLoading: boolean, disableBuyButton: boolean, purchaseButtonTitleClassName: string }) => {
  if (purchaseButtonLoading) return classnames(purchaseButtonTitleClassName,  classes.purchaseButtonLoading);
  if (disableBuyButton) {
    return classnames(purchaseButtonTitleClassName, classes.disableBuyButton);
  }
  return purchaseButtonTitleClassName;
};

export interface PurchaseButtonProps {
  clientAvailableCredit: string;
  currentPaymentSources?: { kind: string, errorText?: string, id: number, context: object };
  purchaseDetails: Purchase.Details;
  updatePurchaseDetails: (params: object) => void;
  userId: number;
  purchaseAmount: number;
  onPurchaseComplete: () => void;
  forMobileBuyBtnOnClick?: (requiredCredit: number) => void;
  purchaseButtonTitle: React.ReactNode | string;
  purchaseButtonTitleClassName?: string;
  purchaseButtonLoading?: boolean;
}

function PurchaseButton({
  clientAvailableCredit, currentPaymentSources, purchaseDetails, updatePurchaseDetails, userId, onPurchaseComplete, forMobileBuyBtnOnClick, purchaseAmount, purchaseButtonTitle, purchaseButtonTitleClassName,
  purchaseButtonLoading
}: PurchaseButtonProps) {
  const { t } = useTranslation();
  const location = useLocation();

  const {
    price, purchaseType, url, requiredCredit, fees
  } = purchaseDetails;

  const hasEnoughtCredit = parseFloat(clientAvailableCredit) >= price;

  const { purchaseFees } = fees;

  useEffect(() => {
    if (userId && purchaseAmount) {
      updatePurchaseDetails({
        purchaseType: purchaseDetailsTypes.ADVISOR_BONUS,
        details: {
          price: purchaseAmount
        }
      });
    }
  }, [userId, purchaseAmount]);

  const renderBuyBtnContent = () => {
    if (purchaseButtonLoading) return <LoadingIndicator />;
    return purchaseButtonTitle;
  };

  const renderBuyBtn = ({ onClick }: { onClick?: (params: object) => void }) => {
    const disableBuyButton = hasEnoughtCredit ? false : (
      !currentPaymentSources || !!currentPaymentSources?.errorText
      || !purchaseAmount
      || (currentPaymentSources?.kind === 'ap' && !applePayIsSupported()));

    return (
      <div
        className={ getForMobileBuyBtnClassName({ purchaseButtonLoading, disableBuyButton, purchaseButtonTitleClassName }) }
        onClick={ onClick }
        onKeyDown={ onClick }
        data-testid="purchase-button-buy-btn"
      >
        { renderBuyBtnContent() }
      </div>
    );
  };

  const renderBtnContent = () => {
    const requiredCreditToFixed = (requiredCredit || 0).toFixed(2);
    const purchaseURLClickSource = getPurchaseClickSource(purchaseType, location);
    const purchaseURL = getPurchaseURL({
      url, requiredCredit: requiredCreditToFixed, purchaseType, clickSource: purchaseURLClickSource
    });
    if (currentPaymentSources?.kind === 'cc') {
      return (
        <CreditCardProcessing
          purchaseURL={ purchaseURL }
          onPurchaseComplete={ onPurchaseComplete }
          type={ purchaseType }
          purchaseAmount={ requiredCredit }
          currentPaymentOption={ currentPaymentSources }
          chatType={ Const.chatType.text }
          notShowPurchaseSuccessPoppup
        >
          { renderBuyBtn({}) }
        </CreditCardProcessing>
      );
    }
    if (currentPaymentSources?.kind === 'pp') {
      return (
        <PayPalProcessing
          purchaseURL={ purchaseURL }
          onPurchaseComplete={ onPurchaseComplete }
          type={ purchaseType }
          purchaseAmount={ requiredCredit }
          currentPaymentOption={ currentPaymentSources }
          chatType={ Const.chatType.text }
          notShowPurchaseSuccessPoppup
        >
          { renderBuyBtn({}) }
        </PayPalProcessing>
      );
    }

    if (currentPaymentSources?.kind === 'ap' || currentPaymentSources?.kind === 'am') {
      return (
        <ProcessApplePay
          { ...currentPaymentSources }
          currentPaymentOption={ currentPaymentSources }
          purchaseAmount={ requiredCredit }
          purchaseURL={ purchaseURL }
          chatType={ Const.chatType.text }
          onPurchaseComplete={ onPurchaseComplete }
          type={ purchaseType }
          purchaseFees={ purchaseFees }
          notShowPurchaseSuccessPoppup
        >
          { renderBuyBtn({}) }
        </ProcessApplePay>
      );
    }
    return renderBuyBtn({});
  };

  const {
    clickSource, context, purchaseScreen
  } = toCamelCase(queryString.parse(window.location.search));

  const [processing, setProcessing] = useState({ isProcessing: false, processingResponse: null });

  const processingStatus = useMemo(() => (
    <div className={ classes.processApplePayMobileProcessingStatusHolder }>
      <div className={ classes.processApplePayMobileProcessingStatusContainer }>
        <MobileProcessingStatus
          purchaseResponce={ processing?.processingResponse }
          purchaseAmount={ requiredCredit }
          bonus={ 0 }
          isAffiliatePromoPopup={ false }
          isCreditCard
          t={ t }
          type={ purchaseType }
          currentPaymentOption={ currentPaymentSources }
          purchaseFees={ purchaseFees }
          chatType={ contextToChatType[context as keyof typeof contextToChatType] }
        />
      </div>
    </div>
  ), [processing]);

  const onProcessing = (isProcessing: boolean, processingResponse: object) => {
    setProcessing({ isProcessing, processingResponse });
  };

  const renderforMobileBuyBtnContent = () => {
    if (currentPaymentSources?.kind === 'ap' || currentPaymentSources?.kind === 'am') {
      const purchaseClickSource = purchaseScreen && clickSource && getClickSource(clickSource);
      const requiredCreditToFixed = (requiredCredit || 0).toFixed(2);

      const purchaseURL = getPurchaseURL({
        url, requiredCredit: requiredCreditToFixed, purchaseType, clickSource: purchaseClickSource
      });
      return (
        <>
          <ProcessApplePay
            { ...currentPaymentSources }
            currentPaymentOption={ currentPaymentSources }
            purchaseAmount={ requiredCredit }
            purchaseURL={ purchaseURL }
            chatType={ Const.chatType.text }
            onProcessing={ onProcessing }
            onPurchaseComplete={ forMobileBuyBtnOnClick }
            type={ purchaseType }
            purchaseFees={ purchaseFees }
            clickSource={ clickSource && getClickSource(clickSource) }
            notShowPurchaseSuccessPoppup
          >
            { renderBuyBtn({}) }
          </ProcessApplePay>
          {processing?.isProcessing || processing?.processingResponse ? processingStatus : null}
        </>
      );
    }

    return renderBuyBtn({ onClick: () => forMobileBuyBtnOnClick(requiredCredit) });
  };

  const renderPurchaseButtonButtonContent = useCallback(() => {
    if (hasEnoughtCredit) {
      return renderBuyBtn({ onClick: onPurchaseComplete });
    }

    if (forMobileBuyBtnOnClick) return renderforMobileBuyBtnContent();
    return renderBtnContent();
  }, [currentPaymentSources, purchaseDetails, purchaseButtonLoading, processing]);

  const renderPurchaseButtonButton = () => (
    <div style={ { width: '100%' } } data-testid="purchase-button-container">
      { renderPurchaseButtonButtonContent() }
    </div>
  );

  return renderPurchaseButtonButton();
}

export default PurchaseButton;
